import { providerLogger } from '@ragu-components/logger';
import { buildUrl, maybeThrowHttpError, validateStatus } from '@ragu-components/ord-providers';

import type {
  AutoprovisioningAuthentication,
  AutoprovisioningAuthenticationUpdate,
  AutoprovisioningSettings,
  AutoprovisioningSettingsUpdate,
  CreateVoicemail,
  TOTPCreate,
  TOTPRequest,
  PasswordChangeRequest,
  TOTPVerify,
  UserSettingsRead,
  UserSettingsUpdate,
  UpdateVoicemail,
  Voicemail,
  PersonalVoicemailUpdate,
} from '@ragu-components/ord-types';
import type { AxiosInstance } from 'axios';
import type { DownloadFileResponse } from 'src/lib/types';

export const getUserSettings = async (axiosInstance: AxiosInstance, realmId: string): Promise<UserSettingsRead> => {
  providerLogger.debug('getUserSettings');
  const url = buildUrl(`me/settings`, { realmId });
  const response = await axiosInstance.get(url, {});
  maybeThrowHttpError(response);
  return response.data;
};

export const updateUserSettings = async (
  axiosInstance: AxiosInstance,
  params: UserSettingsUpdate,
  realmId: string
): Promise<UserSettingsUpdate> => {
  providerLogger.debug('updateUserSettings', params);
  const url = buildUrl(`me/settings`, { realmId });
  const response = await axiosInstance.patch(url, params);
  maybeThrowHttpError(response);
  return response.data;
};

export const getTotp = async (axiosInstance: AxiosInstance, realmId: string): Promise<TOTPRequest> => {
  providerLogger.debug('getTotp');
  const url = buildUrl(`me/totp`, { realmId });
  const response = await axiosInstance.get(url, {});
  maybeThrowHttpError(response);
  return response.data;
};

export const createTotp = async (
  axiosInstance: AxiosInstance,
  params: TOTPCreate,
  realmId: string
): Promise<TOTPCreate> => {
  providerLogger.debug('createTotp', params);
  const url = buildUrl(`me/totp`, { realmId });
  const response = await axiosInstance.post(url, params);
  maybeThrowHttpError(response);
  return params;
};

export const totpVerify = async (
  axiosInstance: AxiosInstance,
  params: TOTPVerify,
  realmId: string
): Promise<TOTPVerify> => {
  providerLogger.debug('totpVerify', params);
  const url = buildUrl(`me/totp/verify`, { realmId });
  const response = await axiosInstance.post(url, params);
  maybeThrowHttpError(response);
  return params;
};

export const updatePassword = async (
  axiosInstance: AxiosInstance,
  params: PasswordChangeRequest,
  realmId: string
): Promise<PasswordChangeRequest> => {
  providerLogger.debug('updatePassword', params);
  const url = buildUrl(`me/password_change`, { realmId });
  const response = await axiosInstance.post(url, params);
  maybeThrowHttpError(response);
  return params;
};

export const downloadFile = async (
  axiosInstance: AxiosInstance,
  params: { resource_id: string; resource: string },
  realmId: string
): Promise<DownloadFileResponse> => {
  providerLogger.debug('downloadFile', params);
  const url = buildUrl(`files`, { realmId, rootPath: 'download' });
  const response = await axiosInstance.get(url, { params, responseType: 'arraybuffer' });
  maybeThrowHttpError(response);
  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename = disposition !== undefined ? disposition.match(/filename="(.+)"/)?.[1] : undefined;
  return { data: response.data, fileType: (response.headers['content-type'] ?? '') as string, filename };
};

export const getUserVoicemail = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  userId: string
): Promise<Voicemail> => {
  const url = buildUrl('voicemail', { realmId, parentResource: 'users', parentId: userId });
  const response = await axiosInstance.get(url, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const createUserVoicemail = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  userId: string,
  params: CreateVoicemail
): Promise<Voicemail> => {
  const url = buildUrl('voicemail', { realmId, parentResource: 'users', parentId: userId });
  const response = await axiosInstance.post(url, params, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const updateUserVoicemail = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  userId: string,
  params: UpdateVoicemail
): Promise<Voicemail> => {
  const url = buildUrl('voicemail', { realmId, parentResource: 'users', parentId: userId });
  const response = await axiosInstance.patch(url, params, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const getPersonalVoicemail = async (axiosInstance: AxiosInstance, realmId: string): Promise<Voicemail> => {
  const url = buildUrl(`me/voicemail`, { realmId });
  const response = await axiosInstance.get(url, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const updatePersonalVoicemail = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  params: PersonalVoicemailUpdate
): Promise<Voicemail> => {
  const url = buildUrl(`me/voicemail`, { realmId });
  const response = await axiosInstance.patch(url, params, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const createBackup = async (
  axiosInstance: AxiosInstance,
  domainId: string,
  realmId: string
): Promise<{ id: string }> => {
  providerLogger.debug('createBackup', domainId);
  const response = await axiosInstance.post(
    `/api/v1/export/${realmId}/backup`,
    {
      target_domain_id: domainId,
      type: 'configuration',
    },
    { validateStatus }
  );
  maybeThrowHttpError(response);
  return response.data as { id: string };
};

export const createBackupImport = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  realm?: string
): Promise<{ id: string }> => {
  providerLogger.debug('createBackupImport', realmId);
  const response = await axiosInstance.post(`/api/v1/import/${realmId}/backup`, realm !== undefined ? { realm } : {}, {
    validateStatus,
  });
  maybeThrowHttpError(response);
  const location: string | undefined = response.headers.location;
  if (location === undefined || location === '') throw new Error('Missing Location header in response');
  const taskId = location.split('/').pop()?.split('?')[0];
  if (taskId === undefined || taskId === '') throw new Error('Missing task ID in response');
  return { id: taskId };
};

export const createBackupRestore = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  targetDomainId: string
): Promise<{ id: string }> => {
  providerLogger.debug('createBackupRestore', realmId, targetDomainId);
  const response = await axiosInstance.post(
    `/api/v1/restore/${realmId}/backup`,
    { target_domain_id: targetDomainId },
    { validateStatus }
  );
  maybeThrowHttpError(response);
  const location: string | undefined = response.headers.location;
  if (location === undefined || location === '') throw new Error('Missing Location header in response');
  const taskId = location.split('/').pop()?.split('?')[0];
  if (taskId === undefined || taskId === '') throw new Error('Missing task ID in response');
  return { id: taskId };
};

export const updateAutoprovisioningAuthentication = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  params: AutoprovisioningAuthenticationUpdate
): Promise<AutoprovisioningAuthentication> => {
  providerLogger.debug('updateAutoprovisioningAuthentication', params);
  const url = buildUrl('autoprovisioning/authentication', { realmId });
  const response = await axiosInstance.patch(url, params, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};

export const updateAutoprovisioningSettings = async (
  axiosInstance: AxiosInstance,
  realmId: string,
  params: AutoprovisioningSettingsUpdate
): Promise<AutoprovisioningSettings> => {
  providerLogger.debug('updateAutoprovisioningSettings', params);
  const url = buildUrl('autoprovisioning/settings', { realmId });
  const response = await axiosInstance.patch(url, params, { validateStatus });
  maybeThrowHttpError(response);
  return response.data;
};
